import { gql } from "@apollo/client";
import { AdminLayout } from "components/admin/admin-layout";
import { TextEditor } from "components/text-editor/text-editor";
import { useInput } from "lib/hooks/use-input";
import {
  useGetBookQuery,
  useUpdateBookMutation,
  GetBookQueryResult,
  useUnlistBookMutation,
} from "lib/graphql-generated";
import { Button } from "components/buttons/button";
import { useState } from "react";
import { EditorState } from "draft-js";
import currency from "currency.js";
import {
  TagSelectorValue,
  SerializeTags,
  TagsSelector,
} from "components/forms/tag-selector";
import { useRouter } from "next/router";
import { Spinner } from "components/spinner";
import {
  CategorySelectorValue,
  SerializeCategories,
  CategoriesSelector,
} from "components/forms/category-selector";
import {
  inputClassNames,
  labelClassNames,
  StandardInput,
} from "components/forms/input";
import {
  appendText,
  convertToDraft,
  saveDraftContent,
} from "components/text-editor/editor-utils";

const GET_BOOK = gql`
  query GetBook($isbn: bigint!) {
    books_by_pk(isbn: $isbn) {
      isbn
      title
      author
      description
      price
      stock
      track_stock
      image
      attributes
      discount_value
      is_discounted
      is_unlisted
      category_books {
        category {
          name
          slug
        }
      }
      tag_books {
        tag {
          id
          text
        }
      }
    }
  }
`;

const UPDATE_BOOK = gql`
  mutation UpdateBook(
    $isbn: bigint!
    $data: books_set_input!
    $tags: [tag_book_insert_input!]!
    $categories: [category_book_insert_input!]!
  ) {
    update_books_by_pk(pk_columns: { isbn: $isbn }, _set: $data) {
      isbn
    }

    delete_tag_book(where: { book_isbn: { _eq: $isbn } }) {
      affected_rows
    }

    insert_tag_book(objects: $tags) {
      affected_rows
    }

    delete_category_book(where: { book_isbn: { _eq: $isbn } }) {
      affected_rows
    }

    insert_category_book(objects: $categories) {
      affected_rows
    }
  }
`;

const UNLIST = gql`
  mutation UnlistBook($isbn: bigint!, $unlist: Boolean) {
    update_books_by_pk(
      pk_columns: { isbn: $isbn }
      _set: { is_unlisted: $unlist }
    ) {
      isbn
    }
  }
`;

export const ExistingBook = () => {
  const router = useRouter();
  const routeIsbn = router.query["isbn"];

  const title = useInput<string>("");
  const description = useInput<EditorState>(null);
  const price = useInput<number>(0);
  const stock = useInput<number>(0);
  const attributes = useInput<string>("");
  const isbn = useInput<string>("");
  const author = useInput<string>("");
  const image = useInput<string>("");
  const tags = useInput<TagSelectorValue[]>([]);
  const isUnlisted = useInput<boolean>(false);
  const [hasDiscount, setHasDiscount] = useState<boolean>(false);
  const discount = useInput<number>(0);
  const categories = useInput<CategorySelectorValue[]>([]);
  const [shouldTrackStock, setTrackStock] = useState<boolean>(false);
  const [hasToggled, setHasToggled] = useState<boolean>(false);

  const [save, { error: errorSaving, loading: saving, data: saveData }] =
    useUpdateBookMutation();

  const handleInit = (data: GetBookQueryResult["data"]) => {
    if (data?.books_by_pk) {
      const book = data.books_by_pk;
      isbn.init(book.isbn);
      title.init(book.title);
      description.init(convertToDraft(book.description));
      isUnlisted.init(book.is_unlisted);
      price.init(currency(book.price, { fromCents: true }).value);
      setHasDiscount(book.is_discounted);
      discount.init(currency(book.discount_value, { fromCents: true }).value);
      stock.init(Boolean(book.stock) ? book.stock : 0);
      setTrackStock(book.track_stock);
      attributes.init(book.attributes);
      author.init(book.author);
      image.init(book.image);
      tags.init(
        SerializeTags(book.tag_books.map((tagObject) => tagObject.tag))
      );
      categories.init(
        SerializeCategories(
          book.category_books.map((cObject) => cObject.category)
        )
      );
    }
  };
  const { data, error, loading, refetch } = useGetBookQuery({
    variables: {
      isbn: routeIsbn,
    },
    skip: !routeIsbn,
    onCompleted: (data) => {
      handleInit(data);
    },
  });

  const [toggleListing] = useUnlistBookMutation({
    variables: {
      isbn: data?.books_by_pk.isbn,
      unlist: !data?.books_by_pk.is_unlisted,
    },
  });

  const isSaveDisabled =
    !(
      title.isModified ||
      author.isModified ||
      price.isModified ||
      image.isModified ||
      attributes.isModified ||
      isUnlisted.isModified ||
      tags.isModified ||
      stock.isModified ||
      description.isModified ||
      categories.isModified ||
      tags.isModified ||
      hasToggled
    ) ||
    loading ||
    saving;

  const handleSave = (e) => {
    e.preventDefault();
    save({
      variables: {
        isbn: isbn.value,
        data: {
          title: title.value,
          author: author.value,
          price: currency(price.value).intValue,
          stock: stock.value,
          track_stock: shouldTrackStock,
          attributes: attributes.value,
          image: image.value,
          is_unlisted: isUnlisted.value,
          is_discounted: hasDiscount,
          discount_value: currency(discount.value).intValue,
          description: saveDraftContent(description.value),
        },
        tags: tags.value.map((tag) => ({
          tag_id: tag.data.id,
          book_isbn: isbn.value,
        })),
        categories: categories.value.map((c) => ({
          category_name: c.data.name,
          book_isbn: isbn.value,
        })),
      },
    }).then(() => {
      setHasToggled(false);
      refetch().then(({ data }) => {
        handleInit(data);
      });
    });
  };

  const handleUnlist = (e) => {
    e.preventDefault();
    if (
      window.confirm(
        `are you sure you want to ${
          data.books_by_pk.is_unlisted ? "list" : "unlist"
        } this book?`
      )
    ) {
      toggleListing().then(() => {
        refetch().then(({ data }) => {
          handleInit(data);
        });
      });
    }
  };

  const handleFindBlurb = async (e) => {
    e.preventDefault();
    let possibleBlurb: string;
    await fetch(
      `https://www.googleapis.com/books/v1/volumes?q=isbn:${data?.books_by_pk.isbn}`,
      {
        method: "GET",
      }
    )
      .then((r) => r.json())
      .then((data) => {
        if (data?.items.length > 0 && !!data?.items[0].volumeInfo) {
          possibleBlurb = data.items[0].volumeInfo.description;
        }
      });

    if (possibleBlurb) {
      description.setValue(appendText(possibleBlurb, description.value));
    }
  };

  return (
    <>
      <AdminLayout page='New Book'>
        <article className='space-y-4'>
          {loading || (!data && !error) ? (
            <Spinner />
          ) : data?.books_by_pk ? (
            <>
              <header className='flex flex-row justify-between'>
                <h1 className='header text-4xl'>Edit Book</h1>
                <section className='space-x-4'>
                  <Button
                    buttonType='primary'
                    buttonSize='md'
                    disabled={isSaveDisabled}
                    onClick={handleSave}
                    loading={saving}>
                    {saving
                      ? "saving"
                      : saveData && isSaveDisabled
                      ? "saved"
                      : "save"}
                  </Button>
                  <Button
                    buttonType='secondary'
                    buttonSize='md'
                    href={`/book/${data?.books_by_pk.isbn}`}>
                    View
                  </Button>
                  <Button
                    buttonType='ghost'
                    buttonSize='md'
                    onClick={() => {
                      router.back();
                    }}>
                    Back
                  </Button>
                </section>
              </header>
              <form
                className='flex flex-row flex-wrap w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 xl:gap-8'
                onSubmit={handleSave}>
                <h2 className='w-full flex flex-col col-span-full row-start-1'>
                  <span>ISBN</span>
                  <span className=''>{isbn.value}</span>
                  <button
                    className={`${
                      data.books_by_pk.is_unlisted
                        ? "text-green-dark"
                        : "text-red-dark"
                    } mr-auto pt-2 hover:underline`}
                    onClick={handleUnlist}>
                    {data.books_by_pk.is_unlisted ? "List" : "Unlist"}
                  </button>
                </h2>

                <StandardInput
                  label='Title'
                  name='title'
                  id='title'
                  type='text'
                  bind={{ ...title.bind }}
                />
                <StandardInput
                  label='Authr'
                  name='author'
                  id='author'
                  type='text'
                  bind={{ ...author.bind }}
                />

                <StandardInput
                  label='Price'
                  name='price'
                  id='price'
                  type='number'
                  bind={{ ...price.bind }}
                />
                <StandardInput
                  label='Attributes'
                  name='attributes'
                  id='attributes'
                  type='text'
                  bind={{ ...attributes.bind }}
                />
                <StandardInput
                  label='Image'
                  name='image'
                  id='image'
                  type='text'
                  bind={{ ...image.bind }}
                />
                <label
                  htmlFor='isUnlisted'
                  className={`${labelClassNames} flex flex-col`}>
                  Unlist
                  <input
                    type='checkbox'
                    name='isUnlisted'
                    id='isUnlisted'
                    className='mr-2 h-8 w-8 mt-1 rounded text-green-default'
                    checked={isUnlisted.value}
                    onChange={(e) => {
                      isUnlisted.setValue(e.target.checked);
                    }}
                  />
                </label>
                <TagsSelector
                  label='Tags'
                  value={tags.value}
                  setValue={tags.setValue}
                />
                <CategoriesSelector
                  label='Categories'
                  value={categories.value}
                  setValue={categories.setValue}
                />
                <section>
                  <label
                    htmlFor='stock'
                    className={`${labelClassNames} w-full flex flex-col`}>
                    Stock
                    <input
                      type='number'
                      name='stock'
                      id='stock'
                      className={inputClassNames}
                      {...stock.bind}
                      disabled={!shouldTrackStock}
                    />
                  </label>
                  <label htmlFor='useStock' className={labelClassNames}>
                    <input
                      type='checkbox'
                      name='useStock'
                      id='useStock'
                      className='mr-2 '
                      checked={shouldTrackStock}
                      onChange={(e) => {
                        setTrackStock(e.target.checked);
                        setHasToggled(true);
                        if (e.target.checked) {
                          stock.setValue(0);
                        }
                      }}
                    />
                    Track Stock
                  </label>
                </section>
                <section>
                  <label
                    htmlFor='discount'
                    className={`${labelClassNames} w-full flex flex-col`}>
                    Discount
                    <input
                      type='number'
                      name='discount'
                      id='discount'
                      className={inputClassNames}
                      {...discount.bind}
                      disabled={!hasDiscount}
                    />
                  </label>
                  <label htmlFor='useDiscount' className={labelClassNames}>
                    <input
                      type='checkbox'
                      name='useDiscount'
                      id='useDiscount'
                      className='mr-2 '
                      checked={hasDiscount}
                      onChange={(e) => {
                        setHasDiscount(e.target.checked);
                        setHasToggled(true);
                        if (e.target.checked) {
                          discount.setValue(0);
                        }
                      }}
                    />
                    Apply Discount
                  </label>
                </section>
                <label htmlFor='description' className='w-full col-span-full'>
                  Description{" "}
                  <TextEditor
                    state={description.value}
                    setState={description.setValue}
                  />
                </label>
              </form>
              <Button buttonType='ghost' onClick={handleFindBlurb}>
                Try and find blurb
              </Button>
            </>
          ) : (
            <section>This book does not exist</section>
          )}
        </article>
      </AdminLayout>
    </>
  );
};

export default ExistingBook;
